import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type RestrictionEvent,
  type JustifyRestrictionEventPayload,
} from 'contracts/restricted-products/restricted-products';
import { RESTRICTION_EVENTS_NAME_MAP } from 'constants/restricted-products/restricted-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { InputGs, TextareaGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';

type Ref = {
  open: (event: RestrictionEvent) => void;
  close: () => void;
};

type Props = {
  onSubmit: (payload: JustifyRestrictionEventPayload) => void;
  loading?: boolean;
};

export type JustifyProductRestrictionDrawerRef = Ref;

export const JustifyEventDrawer = React.forwardRef<Ref, Props>(
  ({ loading, onSubmit }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [event, setEvent] = React.useState<RestrictionEvent>();

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        justification: '',
      },
    });

    const onOpenChange = React.useCallback(
      (open: boolean, event?: RestrictionEvent) => {
        setOpen(open);
        setEvent(event);
        form.reset();
        event && form.setValue('eventId', event.id);
      },
      [form],
    );

    const handleSubmit = React.useCallback(
      (formData: FormSchema) => {
        onSubmit({
          eventId: formData.eventId,
          justification: formData.justification,
          file: formData.file?.[0],
        });
      },
      [onSubmit],
    );

    React.useImperativeHandle(ref, () => {
      return {
        open: (event) => onOpenChange(true, event),
        close: () => onOpenChange(false),
      };
    }, [onOpenChange]);

    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Content>
          <div className="max-w-[480px] w-full mx-auto">
            <Drawer.Header className={'mb-6'}>
              <Drawer.Title className={'font-GilroyBold text-gray-600'}>
                Justificar evento de {RESTRICTION_EVENTS_NAME_MAP[event?.action || 'block']}
              </Drawer.Title>
            </Drawer.Header>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className={'flex flex-col gap-4 px-4 pb-10'}
              >
                <FormField
                  control={form.control}
                  name={'justification'}
                  render={({ field }) => (
                    <TextareaGs
                      {...field}
                      label={'Justificativa'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'file'}
                  render={() => (
                    <InputGs
                      type={'file'}
                      label={'Imagem'}
                      {...form.register('file')}
                    />
                  )}
                />
                <div className={'grid grid-cols-2 gap-4'}>
                  <Button.Root
                    type={'button'}
                    size={'sm'}
                    variant={'secondary'}
                    onClick={() => onOpenChange(false)}
                  >
                    Cancelar
                  </Button.Root>
                  <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                    {loading ? <ActivityIndicator /> : 'Salvar'}
                  </Button.Root>
                </div>
              </form>
            </Form>
          </div>
        </Drawer.Content>
      </Drawer.Root>
    );
  },
);
JustifyEventDrawer.displayName = 'JustifyEventDrawer';
